import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { togglePanel, setPanelOpen } from '../redux/slices/notificationsSlice';
import { fetchUnreadCount } from '../redux/thunk/notificationsThunk';
import ClickableDiv from './common/ClickableDiv';
import { track } from '../utils/analytics';
import './NotificationBell.scss';

// Shared event so the three header overlays (notifications, user menu,
// mobile hamburger) auto-close each other when any one of them opens.
export const NAV_OVERLAY_EVENT = 'mixgoat:nav-overlay-open';

const NotificationBell = () => {
    const dispatch = useDispatch();
    const { unreadCount, panelOpen } = useSelector(state => state.notifications);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

    // Fetch on mount and poll every 60 seconds
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchUnreadCount());
            const interval = setInterval(() => {
                dispatch(fetchUnreadCount());
            }, 60 * 1000);
            return () => clearInterval(interval);
        }
    }, [isAuthenticated, dispatch]);

    // No refetch on panel close here: NotificationPanel marks everything read
    // on close and that thunk re-syncs the count once the server confirms.
    // Fetching here as well raced that PATCH and could flash the stale badge.

    // Close when another nav overlay announces it's opening
    useEffect(() => {
        const onOtherOpen = (e) => {
            if (e.detail?.source === 'notifications') return;
            if (panelOpen) dispatch(setPanelOpen(false));
        };
        window.addEventListener(NAV_OVERLAY_EVENT, onOtherOpen);
        return () => window.removeEventListener(NAV_OVERLAY_EVENT, onOtherOpen);
    }, [panelOpen, dispatch]);

    if (!isAuthenticated) return null;

    const handleClick = () => {
        if (!panelOpen) {
            window.dispatchEvent(new CustomEvent(NAV_OVERLAY_EVENT, { detail: { source: 'notifications' } }));
            track('notifications_opened', { unread_count: unreadCount });
        }
        dispatch(togglePanel());
    };

    return (
        <ClickableDiv
            className={`notification-bell ${panelOpen ? 'active' : ''}`}
            onClick={handleClick}
            title="Notifications"
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
        </ClickableDiv>
    );
};

export default NotificationBell;
